import { PlatformUserBase } from 'models/user'
import { logger } from 'utils/logging'

// Not 100% defined, just for purpose of ts-check
interface BeckaService {
  settings: {
    language: string
  }
  init: (token: string, name: string, url: string, options: object) => void
  isLocalStorageAvailable: boolean
  userVerificationAPI: {
    setIdentityToken: (params: { identityTokenType: string, identityToken: string }) => unknown
    clearSession: (shouldEndSession?: boolean) => Promise<void> | void
  }
  utilAPI: {
    launchChat: () => void
  }
  prechatAPI: {
    /**
     * All prechat fields can be set as public using the function setVisiblePrechatFields, 
     * for testing purposes, has to be removed in production.
     * DOCS: https://help.salesforce.com/s/articleView?id=service.miaw_pre_chat_api_2.htm&type=5
     */
    setVisiblePrechatFields: (fields: {
      [key: string]: {
        value: string
        isEditableByEndUser: boolean
      }
    }) => void
    setHiddenPrechatFields: (fields: { [key: string]: string }) => void
  }
}

declare global {
  interface Window {
    embeddedservice_bootstrap: BeckaService
  }
}

const BECKA_ID = process.env.REACT_APP_BECKA_ID
const BECKA_NAME = process.env.REACT_APP_BECKA_NAME
const BECKA_URL = process.env.REACT_APP_BECKA_URL
const BECKA_SCRT_URL = process.env.REACT_APP_BECKA_SCRT_URL

const BECKA_SCRIPT_PATH = '/assets/js/bootstrap.min.js'

/**
 * Long enough for a slow connection, short enough to notice the script not running: Cookiebot's
 * auto blocking mode can hold a script back (type rewritten to text/plain) until consent, and then
 * neither `load` nor `error` fires until the user accepts. Only reported: the load keeps waiting, so
 * a late script or a consent given later still starts the chat.
 */
export const BECKA_SCRIPT_TIMEOUT_MS = 15_000

const LOG_CONTEXT = { component: 'becka' }

let beckaScript: Promise<void> | undefined
let isInitialized = false
let isMessagingReady = false

const beckaScriptSrc = (): string | undefined => {
  if (!BECKA_URL) return undefined
  // HTMLScriptElement.src is the normalised URL, so compare against the same form.
  try {
    return new URL(`${BECKA_URL}${BECKA_SCRIPT_PATH}`).href
  } catch {
    return undefined
  }
}

/**
 * Loads Salesforce's messaging bootstrap script from the org in `REACT_APP_BECKA_URL`, once per page.
 * It used to be a static tag in index.html, which fetched the production org's script on every
 * page, embeds and dev included, before the app had decided to show the chat (ISSUE-4266).
 *
 * A failed load is reported and forgotten, so the next call tries again. One still pending after
 * `BECKA_SCRIPT_TIMEOUT_MS` is reported and keeps waiting.
 */
export function loadBeckaScript(): Promise<void> {
  if (beckaScript) return beckaScript
  // Already on the page: running the script a second time only makes Salesforce log an error.
  if (window.embeddedservice_bootstrap) return (beckaScript = Promise.resolve())

  const src = beckaScriptSrc()
  if (!src) return Promise.reject(new Error(`BECKA_URL is undefined or invalid: ${BECKA_URL}`))

  const script = document.createElement('script')
  const attempt = new Promise<void>((resolve, reject) => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    const finish = (error?: Error) => {
      clearTimeout(timeout)
      document.removeEventListener('load', settle, true)
      document.removeEventListener('error', settle, true)
      if (!error) return resolve()
      script.remove()
      beckaScript = undefined
      reject(error)
    }
    // Cookiebot's auto blocking mode may run a copy of the element instead of the one appended here,
    // so the events are taken from any script with this src. They do not bubble but do pass through
    // the capture phase on the document.
    const settle = (event: Event) => {
      if (!(event.target instanceof HTMLScriptElement) || event.target.src !== src) return
      if (event.type === 'load') return finish()
      logger.warn('Becka script failed to load', undefined, {
        context: { ...LOG_CONTEXT, action: 'loadScript', metadata: { host: new URL(src).host, outcome: 'error' } },
      })
      finish(new Error(`Failed to load ${src}`))
    }
    // Reported, not settled: removing the tag would not cancel a download already under way, and a
    // retry would then run the script twice.
    timeout = setTimeout(() => {
      logger.warn('Becka script neither loaded nor failed in time', undefined, {
        context: {
          ...LOG_CONTEXT,
          action: 'loadScript',
          metadata: { host: new URL(src).host, outcome: 'timeout', timeoutMs: BECKA_SCRIPT_TIMEOUT_MS },
        },
      })
    }, BECKA_SCRIPT_TIMEOUT_MS)
    document.addEventListener('load', settle, true)
    document.addEventListener('error', settle, true)
  })
  beckaScript = attempt

  script.type = 'text/javascript'
  script.async = true
  script.src = src
  document.head.appendChild(script)
  return attempt
}

/**
 * Ends the Salesforce messaging session of whoever was logged in (logout, login, expired session).
 *
 * Salesforce's `clearSession()` rejects until `onEmbeddedMessagingReady` has fired, that is until
 * `init` has run, so loading the script here would not help. Where the chat is not ready, the
 * session's first-party web storage (`<orgId>_WEB_STORAGE` in local and session storage, the key
 * bootstrap.min.js itself clears) is removed directly. Synchronous, so it never delays a redirect.
 */
export function clearBeckaSession(): void {
  if (isMessagingReady) {
    try {
      Promise.resolve(window.embeddedservice_bootstrap?.userVerificationAPI?.clearSession?.()).catch((error) => {
        logger.warn('Becka clearSession failed', error, { context: { ...LOG_CONTEXT, action: 'clearSession' } })
      })
    } catch (error) {
      logger.warn('Becka clearSession failed', error, { context: { ...LOG_CONTEXT, action: 'clearSession' } })
    }
  }
  if (!BECKA_ID) return
  const key = `${BECKA_ID}_WEB_STORAGE`
  try {
    window.localStorage.removeItem(key)
    window.sessionStorage.removeItem(key)
  } catch (error) {
    logger.warn('Becka web storage could not be cleared', error, { context: { ...LOG_CONTEXT, action: 'clearSession' } })
  }
}

/** Resolves to whether the chat was initialised. Never call it on an embed route (ISSUE-2310). */
export async function initBecka(token: string, userData: PlatformUserBase, isoLanguage: string = 'en_US'): Promise<boolean> {
  if (!BECKA_ID) throw new Error('BECKA_ID is undefined')
  if (!BECKA_NAME) throw new Error('BECKA_NAME is undefined')
  if (!BECKA_URL) throw new Error('BECKA_URL is undefined')
  if (!BECKA_SCRT_URL) throw new Error('BECKA_SCRT_URL is undefined')

  try {
    await loadBeckaScript()

    // init() a second time would start a second messaging client on the same page.
    if (isInitialized) return true

    // Set the language before initializing
    window.embeddedservice_bootstrap.settings.language = isoLanguage // For example, 'en' or 'en-US'

    // Set up the event listener for when the embedded messaging is ready
    window.addEventListener('onEmbeddedMessagingReady', () => {
      isMessagingReady = true

      // Set the identity token for user verification
      window.embeddedservice_bootstrap.userVerificationAPI.setIdentityToken({
        identityTokenType: 'JWT',
        identityToken: token,
      })

      // Set hidden pre-chat fields
      window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
        // Use the pre-chat API names as keys
        'PlatformPageURL': window.location.href,
        'PlatformUserID': userData.publicId?.toString() ?? '',
        'PlatformUserEmail': userData.email ?? '',
      })
    }, { once: true })

    // Initialize the embedded messaging
    window.embeddedservice_bootstrap.init(
      BECKA_ID,
      BECKA_NAME,
      BECKA_URL,
      {
        scrt2URL: BECKA_SCRT_URL,
      }
    )
    isInitialized = true

    return true
  } catch (err) {
    // A failed load is already reported by loadBeckaScript.
    if (isInitialized || window.embeddedservice_bootstrap) {
      logger.error('Becka chat init failed', err, { context: { ...LOG_CONTEXT, action: 'init' } })
    }
    return false
  }
}
